/**
 * Accès aux ponts natifs utilisés par la mise à jour guidée (présents dans tous les
 * APK du parc : VisionPOSUpdater depuis toujours, VisionPOSRemoteOps depuis 1.5.9.16).
 */

import type { GuidedManifest, UpdaterEvent } from '@/lib/guided-update/policy';

type GuidedNativeWindow = Window & {
  VisionPOSUpdater?: { getCurrentVersionCode?: () => number; triggerUpdate?: () => void };
  VisionPOSRemoteOps?: { killTask?: () => void };
};

const nativeWindow = (): GuidedNativeWindow => window as GuidedNativeWindow;

/** versionCode de l'APK, ou null hors APK (navigateur, PWA, pont absent). */
export function readNativeVersionCode(): number | null {
  try {
    const code = Number(nativeWindow().VisionPOSUpdater?.getCurrentVersionCode?.());
    return Number.isFinite(code) && code > 0 ? code : null;
  } catch {
    return null;
  }
}

/** Relance la vérification native : télécharge, ou ouvre « Installer » si le fichier est là. */
export function triggerNativeUpdate(): boolean {
  try {
    const updater = nativeWindow().VisionPOSUpdater;
    if (!updater?.triggerUpdate) return false;
    updater.triggerUpdate();
    return true;
  } catch {
    return false;
  }
}

export function canCloseApp(): boolean {
  return typeof nativeWindow().VisionPOSRemoteOps?.killTask === 'function';
}

/** Ferme l'appli (finishAffinity + exit) : sa réouverture reprend l'installation. */
export function closeApp(): boolean {
  try {
    const ops = nativeWindow().VisionPOSRemoteOps;
    if (!ops?.killTask) return false;
    ops.killTask();
    return true;
  } catch {
    return false;
  }
}

export function subscribeUpdaterEvents(onEvent: (event: UpdaterEvent) => void): () => void {
  const handler = (e: Event) => {
    const detail: unknown = (e as CustomEvent<unknown>).detail;
    if (detail && typeof detail === 'object') onEvent(detail as UpdaterEvent);
  };
  window.addEventListener('vp-updater', handler);
  return () => window.removeEventListener('vp-updater', handler);
}

/** Manifeste servi par le même déploiement que la page (public/android/version.json). */
export async function fetchGuidedManifest(): Promise<GuidedManifest | null> {
  try {
    const res = await fetch(`/android/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body: unknown = await res.json();
    if (!body || typeof body !== 'object') return null;
    const { versionCode, forceUpdate, silent } = body as Record<string, unknown>;
    if (typeof versionCode !== 'number') return null;
    return { versionCode, forceUpdate: forceUpdate === true, silent: silent === true };
  } catch {
    return null;
  }
}
